import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getClientEnv } from "@/const";
import { Mail, Lock } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import "./Login.css";

export default function Login() {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (videoRef.current) {
      // Make water move slowly (0.75 for light motion)
      videoRef.current.playbackRate = 0.75;
    }
  }, []);

  const authProvider = getClientEnv().authProvider;
  const { user, isAuthenticated, refresh } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Redirect already logged-in users to main app with replace to avoid leaving /login in history
  useEffect(() => {
    if (isAuthenticated && user) {
      window.history.replaceState(null, '', "/");
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  }, [isAuthenticated, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Login failed");
      }

      // Refresh auth hook to update user state
      await refresh();

      // Successful login - redirect to main app with full page replace to prevent
      // navigating back to restricted admin pages via browser Back button after role changes
      window.location.replace("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid login");
    } finally {
      setLoading(false);
    }
  };

  // Show error if not using custom auth provider
  if (authProvider !== "custom") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              Login page is only available when using custom authentication.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-neutral-950 text-neutral-50 overflow-hidden">
      {/* Background video */}
      <div className="pointer-events-none absolute inset-0">
        <video
          ref={videoRef}
          className="h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
        >
          <source src="/media/water-bg.mp4" type="video/mp4" />
        </video>
        {/* dark overlay so card/header are readable */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black/90" />
      </div>

      {/* Foreground content */}
      <div className="relative z-10 flex min-h-screen flex-col">
        {/* Header + tagline */}
        <header className="px-4 pt-10 pb-6">
          <div className="mx-auto max-w-5xl text-left space-y-1">
            <h1 className="text-3xl font-semibold text-neutral-50">
              City of Reedley
            </h1>
            <p className="text-base text-neutral-300">
              Water & Wastewater Operations Portal
            </p>
            <p className="text-sm font-medium text-amber-400">
              Pilot System — Operational Records Platform
            </p>
            <p className="text-xs text-neutral-400">
              Used for evaluation and continuous improvement
            </p>
          </div>
        </header>

        {/* Centered login card */}
        <main className="flex flex-1 items-start justify-center px-4 pb-10">
          <div className="mt-4 flex w-full max-w-md items-start justify-center">
            <div className="w-full bg-neutral-900/90 border border-neutral-700 rounded-2xl shadow-2xl px-8 py-8 space-y-6">
        {/* Icon + title + subtitle */}
        <div className="flex flex-col items-center space-y-4">
          <div className="h-12 w-12 rounded-full bg-amber-500/15 flex items-center justify-center">
            <span className="text-2xl" aria-hidden="true">💧</span>
          </div>

          <div className="text-center space-y-1">
            <h1 className="text-2xl font-semibold text-white">
              City Water Well Logging
            </h1>
            <p className="text-sm text-neutral-300">
              Professional Water Operations Management
            </p>
          </div>

          <p className="text-sm text-neutral-400 text-center">
            Track daily logs, monitor chlorine levels, and generate comprehensive
            reports for municipal water well operations.
          </p>
        </div>

        {/* System status */}
        <div className="flex items-center justify-center space-x-2 text-sm">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
          </span>
          <span className="text-neutral-300">System Online</span>
        </div>

        {/* Sign In form section */}
        <div className="mt-6 space-y-4">
          <h2 className="text-lg font-semibold text-white text-center">
            Sign In
          </h2>
          <p className="text-sm text-neutral-400 text-center">
            Enter your email and password to access your account.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-200">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-600 text-slate-100 placeholder-slate-400"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-200">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-600 text-slate-100 placeholder-slate-400"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            <button
              type="submit"
              className="w-full inline-flex items-center justify-center rounded-lg bg-amber-500 text-neutral-900 font-medium py-2.5 text-sm hover:bg-amber-400 transition focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 focus:ring-offset-neutral-900 disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="pt-4 border-t border-neutral-800">
          <p className="text-center text-xs text-neutral-500">
            Powered by <span className="font-semibold">Loveitio</span>
          </p>
        </div>
        </div>
        </div>
        </main>
      </div>
    </div>
  );
}
